import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Compass, AlertOctagon, AlertTriangle, Info, CheckCircle2, CircleDashed,
  Clock, HelpCircle, LifeBuoy, Loader2, Bell } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PRIORITY = {
  critical: { label: "Critical", cls: "bg-red-100 text-red-700 border-red-200", Icon: AlertOctagon },
  high: { label: "High", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: AlertTriangle },
  normal: { label: "Normal", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: Info },
  informational: { label: "Info", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Info },
};

const PILLAR_STATE = {
  complete: { label: "Complete", cls: "text-emerald-700 bg-emerald-50", Icon: CheckCircle2 },
  on_track: { label: "On track", cls: "text-emerald-700 bg-emerald-50", Icon: CheckCircle2 },
  in_progress: { label: "In progress", cls: "text-blue-700 bg-blue-50", Icon: CircleDashed },
  attention: { label: "Attention", cls: "text-amber-800 bg-amber-50", Icon: AlertTriangle },
  blocked: { label: "Blocked", cls: "text-red-700 bg-red-50", Icon: AlertOctagon },
  not_started: { label: "Not started", cls: "text-slate-600 bg-slate-100", Icon: CircleDashed },
  not_applicable: { label: "N/A", cls: "text-slate-400 bg-slate-50", Icon: CircleDashed },
};

const PILLAR_LABELS = {
  business_verification: "Business verification", representative_authority: "Representative authority",
  compliance: "Compliance", contracts_signatures: "Contracts & signatures",
  professional_routing: "Professional routing", logistics_carrier: "Logistics & carrier",
  pickup: "Pickup", chain_of_custody: "Chain of custody", delivery_acceptance: "Delivery & acceptance",
  escrow_funding: "Escrow & funding", holds: "Holds", disputes: "Disputes",
  release_readiness: "Release readiness",
};

const HELP_TOPICS = [
  ["why_is_the_deal_blocked", "Why is this deal blocked?"],
  ["what_do_i_need_to_do_next", "What do I do next?"],
  ["who_must_act_next", "Who must act next?"],
  ["why_cant_funds_release", "Why can't funds release?"],
];

function fmtDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export default function CoachPanel({ deal, onNavigate }) {
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [help, setHelp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await api.post(`/deals/${deal.deal_id}/reminders/sync`).catch(() => {});
      const [c, r] = await Promise.all([
        api.get(`/deals/${deal.deal_id}/coach`),
        api.get(`/reminders`, { params: { deal_id: deal.deal_id } }),
      ]);
      setData(c.data);
      setReminders(r.data.reminders || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [deal.deal_id]);

  useEffect(() => { load(); }, [load]);

  const openAction = (rec) => {
    api.post(`/deals/${deal.deal_id}/coach/events`, { action_id: rec.action_id, event: "opened" }).catch(() => {});
    if (onNavigate) onNavigate(rec.route);
    else toast.info(`Open: ${rec.route}`);
  };

  const askHelp = async (topic) => {
    setBusy(`help-${topic}`);
    try {
      const { data: h } = await api.get(`/deals/${deal.deal_id}/coach/help`, { params: { topic } });
      setHelp({ topic, ...h });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const handoff = async () => {
    const question = window.prompt("Describe what you need help with:");
    if (!question) return;
    setBusy("handoff");
    try {
      const { data: h } = await api.post(`/deals/${deal.deal_id}/coach/handoff`, { question });
      toast.success(`Routed to ${h.handoff.recommended_channel.replace(/_/g, " ")} — the team has your context.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const ackReminder = (id) => api.post(`/reminders/${id}/acknowledge`).then(load).catch(() => {});

  if (loading) return <div className="mt-6 py-8 flex justify-center" data-testid="coach-loading"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!data) return null;

  return (
    <div className="mt-6 border-t border-slate-200 pt-5 space-y-6" data-testid="coach-panel">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Compass size={16} /> Deal Coach</h4>
        <button data-testid="coach-handoff-btn" onClick={handoff} disabled={busy === "handoff"}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-400">
          <LifeBuoy size={13} /> Ask for help
        </button>
      </div>

      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="coach-summary">{data.summary}</p>

      {/* Reminders */}
      {reminders.length > 0 && (
        <div data-testid="coach-reminders">
          <h5 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Bell size={13} /> Reminders</h5>
          <ul className="space-y-1.5">
            {reminders.map((r) => {
              const p = PRIORITY[r.severity] || PRIORITY.normal;
              return (
                <li key={r.reminder_id} data-testid={`reminder-${r.reminder_id}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${p.cls}`}><p.Icon size={11} /> {r.escalation_stage}</span>
                    <span className="truncate text-slate-700">{r.text}</span>
                  </span>
                  <button data-testid={`reminder-ack-${r.reminder_id}`} onClick={() => ackReminder(r.reminder_id)}
                    className="shrink-0 rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-500 hover:border-slate-400">Ack</button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      <div data-testid="coach-recommendations">
        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Next best actions</h5>
        {data.recommendations.length === 0 && <p className="text-sm text-slate-400">No action required from you right now.</p>}
        <div className="space-y-2.5">
          {data.recommendations.map((rec, i) => {
            const p = PRIORITY[rec.priority] || PRIORITY.normal;
            return (
              <div key={i} data-testid={`coach-rec-${rec.action_id}`} className="rounded-lg border border-slate-200 p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${p.cls}`}><p.Icon size={11} /> {p.label}</span>
                      <span className="font-semibold text-[#0A0F1C] text-sm">{rec.title}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{rec.explanation}</p>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-400">
                      <span>Responsible: <span className="text-slate-600">{rec.responsible_role}</span></span>
                      {rec.due_date && <span className="flex items-center gap-1"><Clock size={10} /> {fmtDate(rec.due_date)}</span>}
                      {rec.blocking_requirement && <span>Blocked by: <span className="text-amber-700">{rec.blocking_requirement}</span></span>}
                      {rec.required_evidence?.length > 0 && <span>Evidence: {rec.required_evidence.join(", ")}</span>}
                    </div>
                    {rec.after_completion && <p className="mt-1 text-[11px] text-emerald-700">✓ After: {rec.after_completion}</p>}
                  </div>
                  {rec.action_id !== "contact_support" && (
                    <button data-testid={`coach-action-${rec.action_id}`} onClick={() => openAction(rec)}
                      className="shrink-0 rounded-md bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors">
                      {rec.title}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {data.other_pending_actions?.length > 0 && (
          <div className="mt-3 rounded-md bg-slate-50 p-2.5 text-[11px] text-slate-500" data-testid="coach-other-pending">
            <span className="font-semibold">Waiting on others: </span>
            {data.other_pending_actions.map((o, i) => (
              <span key={i}>{o.title} ({o.responsible_role}){i < data.other_pending_actions.length - 1 ? "; " : ""}</span>
            ))}
          </div>
        )}
      </div>

      {/* Readiness checklist */}
      <div data-testid="coach-readiness">
        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Deal readiness</h5>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {Object.entries(data.readiness).map(([key, val]) => {
            const st = PILLAR_STATE[val.status] || PILLAR_STATE.not_applicable;
            return (
              <div key={key} data-testid={`pillar-${key}`}
                className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm">
                <span className="text-slate-700">{PILLAR_LABELS[key] || key}</span>
                <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>
                  <st.Icon size={12} /> {st.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Contextual help */}
      <div data-testid="coach-help">
        <h5 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><HelpCircle size={13} /> Ask the coach</h5>
        <div className="flex flex-wrap gap-1.5">
          {HELP_TOPICS.map(([topic, label]) => (
            <button key={topic} data-testid={`help-${topic}`} onClick={() => askHelp(topic)} disabled={busy === `help-${topic}`}
              className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:border-slate-400">{label}</button>
          ))}
        </div>
        {help && (
          <div className="mt-2 rounded-md bg-blue-50 p-3 text-sm text-slate-700" data-testid="coach-help-answer">
            {typeof help.answer === "string" ? help.answer : JSON.stringify(help.answer)}
          </div>
        )}
      </div>
    </div>
  );
}
